/**
 * /pricing — Pricing plans for API to MCP Converter
 *
 * Three tiers: Free · Pro · Pay-per-use
 * Premium design with animated cards, feature comparison, FAQ.
 */

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import {
  Check, X, Zap, Crown, Star, Sparkles, ArrowRight,
  Clock, Infinity, Shield, BarChart3, HelpCircle,
  ChevronDown, CreditCard,
} from "lucide-react";
import TopHeader from "../components/TopHeader";
import Footer from "../components/Footer";
import {
  getSubscriptionStatus, syncPlanFromStripe,
  startProCheckout, startPpuCheckout,
} from "../lib/subscriptionApi";
import { openPaddleTransactionCheckout } from "../lib/paddleCheckout";
import { fetchSessionUser, type SessionUser } from "../lib/sessionUser";
import { openAuthModal } from "../lib/openAuthModal";

// Checkout intent — the user is already authenticated via NextAuth when this
// is set, so we go straight to Paddle (no separate OTP step).
type CheckoutModalPlan = {
  product: "pro" | "ppu";
  email: string;
  ppuQuantity?: number;
};

// ── Feature matrix ────────────────────────────────────────────────────────────

const FEATURES = [
  { label: "MCP conversions / month",    free: "3",              pro: "Unlimited" },
  { label: "Hosted MCP lifetime",        free: "7 days",         pro: "Forever" },
  { label: "Live remote MCP endpoint",   free: true,             pro: true },
  { label: "SSE + Streamable HTTP",      free: true,             pro: true },
  { label: "All API auth schemes",       free: true,             pro: true },
  { label: "Per-MCP usage analytics",    free: false,            pro: true },
  { label: "Priority API access",        free: false,            pro: true },
  { label: "Remove expiry banners",      free: false,            pro: true },
  { label: "Early access to new tools",  free: false,            pro: true },
  { label: "Email support",              free: false,            pro: true },
];

const FAQ = [
  {
    q: "What counts as a conversion?",
    a: "Each time you submit an API spec (via file, URL, or paste) and receive a live MCP endpoint, that's one conversion. Previewing a spec without hosting it does not count.",
  },
  {
    q: "What happens to my MCPs after 7 days on the free plan?",
    a: "The live endpoint expires and stops responding. Your spec and conversion history are retained, so you can re-convert at any time (subject to your monthly limit).",
  },
  {
    q: "Can I upgrade mid-month?",
    a: "Yes. You'll be charged immediately and your plan upgrades instantly — all future MCPs will never expire.",
  },
  {
    q: "What is pay-per-use?",
    a: "Buy a pack of extra conversions without committing to a monthly plan. Each pack adds 10 conversions that never expire. Perfect if you only need occasional bursts.",
  },
  {
    q: "What payment methods are supported?",
    a: "Payments are processed by Paddle, which supports credit/debit cards worldwide, PayPal, Apple Pay, and Google Pay.",
  },
  {
    q: "Can I cancel my Pro subscription?",
    a: "Yes, cancel anytime from your Paddle customer portal. Your plan stays active until the end of the billing period.",
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function FeatureValue({ val }: { val: string | boolean }) {
  if (val === true)  return <Check size={18} className="text-emerald-500 mx-auto" />;
  if (val === false) return <X    size={18} className="text-zinc-300 dark:text-zinc-600 mx-auto" />;
  return <span className="text-sm font-medium text-foreground">{val}</span>;
}

// ── FAQ accordion ─────────────────────────────────────────────────────────────

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-muted/40 transition-colors"
      >
        <span className="font-medium text-foreground">{q}</span>
        <ChevronDown
          size={18}
          className={`text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="px-6 pb-5 text-sm text-muted-foreground leading-relaxed">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function PricingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [sessionUser, setSessionUser] = useState<SessionUser | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [currentPlan, setCurrentPlan] = useState<"free" | "pro" | null>(null);
  const [ppuQty, setPpuQty] = useState(10);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);
  const [checkoutPlan, setCheckoutPlan] = useState<CheckoutModalPlan | null>(null);
  // Stores which checkout to resume after the user signs in.
  // Written to sessionStorage so it survives Google OAuth full-page redirects.
  const pendingCheckout = useRef<"pro" | "ppu" | null>(
    (sessionStorage.getItem("pricing_pending_checkout") as "pro" | "ppu" | null) ?? null,
  );
  const setPendingCheckout = (val: "pro" | "ppu" | null) => {
    pendingCheckout.current = val;
    if (val) sessionStorage.setItem("pricing_pending_checkout", val);
    else sessionStorage.removeItem("pricing_pending_checkout");
  };

  const checkoutSuccess = searchParams.get("checkout") === "success";
  const cancelled = searchParams.get("cancelled") === "1";
  const pendingTxn = searchParams.get("_ptxn");

  // Load session — track loading state so we never misfire the auth modal
  useEffect(() => {
    void fetchSessionUser().then(u => {
      setSessionUser(u);
      setSessionLoading(false);
      // Resume pending checkout if user is already signed in on mount
      // (covers Google OAuth full-page redirect returning to this page)
      if (pendingCheckout.current && u?.email) {
        const action = pendingCheckout.current;
        setPendingCheckout(null);
        setCheckoutPlan({
          product: action,
          email: u.email.trim(),
          ppuQuantity: action === "ppu" ? ppuQty : undefined,
        });
      }
    });
    const onUpdate = () => {
      void fetchSessionUser().then(u => {
        setSessionUser(u);
        setSessionLoading(false);
        // If user just signed in and had a pending checkout, resume it now
        if (pendingCheckout.current && u?.email) {
          const action = pendingCheckout.current;
          setPendingCheckout(null);
          setCheckoutPlan({
            product: action,
            email: u.email.trim(),
            ppuQuantity: action === "ppu" ? ppuQty : undefined,
          });
        }
      });
    };
    window.addEventListener("auth-session-updated", onUpdate);
    return () => window.removeEventListener("auth-session-updated", onUpdate);
  }, [ppuQty]);

  // Load plan status
  useEffect(() => {
    const email = sessionUser?.email?.trim();
    if (!email) return;
    getSubscriptionStatus(email)
      .then(s => setCurrentPlan(s.plan))
      .catch(() => {});
  }, [sessionUser]);

  // Resume checkout when Paddle redirects here with ?_ptxn=
  useEffect(() => {
    if (!pendingTxn) return;
    let cancelled = false;
    const successUrl = `${window.location.origin}/dashboard?checkout=success`;
    void openPaddleTransactionCheckout(pendingTxn, { successUrl }).then((opened) => {
      if (cancelled) return;
      const next = new URLSearchParams(searchParams);
      next.delete("_ptxn");
      setSearchParams(next, { replace: true });
      if (!opened) {
        setToast({
          msg: "Could not open payment — check PADDLE_CLIENT_TOKEN or try again.",
          type: "error",
        });
      }
    });
    return () => { cancelled = true; };
  }, [pendingTxn, searchParams, setSearchParams]);

  // Legacy ?upgraded=1 — sync before celebrating (no instant fake Pro)
  useEffect(() => {
    const legacyUpgraded = searchParams.get("upgraded") === "1";
    if (!legacyUpgraded) return;
    const email = sessionUser?.email?.trim();
    const next = new URLSearchParams(searchParams);
    next.delete("upgraded");
    setSearchParams(next, { replace: true });
    if (!email) {
      navigate("/dashboard?checkout=success", { replace: true });
      return;
    }
    void syncPlanFromStripe(email).then(({ plan }) => {
      if (plan === "pro") {
        setToast({ msg: "🎉 Welcome to Pro! Your plan is now active.", type: "success" });
        setCurrentPlan("pro");
      } else {
        setToast({
          msg: "Complete payment on the pricing page to activate Pro.",
          type: "error",
        });
      }
    });
  }, [searchParams, sessionUser, setSearchParams, navigate]);

  useEffect(() => {
    if (checkoutSuccess) {
      setToast({
        msg: "Thanks! If Pro isn't active yet, refresh in a moment.",
        type: "success",
      });
      const next = new URLSearchParams(searchParams);
      next.delete("checkout");
      setSearchParams(next, { replace: true });
    }
    if (cancelled) setToast({ msg: "Checkout cancelled — no charge was made.", type: "error" });
  }, [checkoutSuccess, cancelled, searchParams, setSearchParams]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  // When a checkout intent is set, go straight to Paddle.
  // Identity is already proven by the NextAuth session — no separate OTP step.
  useEffect(() => {
    if (!checkoutPlan) return;
    let cancelled = false;
    const run = async () => {
      try {
        if (checkoutPlan.product === "pro") {
          await startProCheckout(checkoutPlan.email);
        } else {
          await startPpuCheckout(checkoutPlan.email, checkoutPlan.ppuQuantity ?? 10);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setToast({
            msg: e instanceof Error ? e.message : "Payment could not start — please try again.",
            type: "error",
          });
        }
      } finally {
        if (!cancelled) setCheckoutPlan(null);
      }
    };
    void run();
    return () => { cancelled = true; };
  }, [checkoutPlan]);

  const handlePro = async () => {
    // Session still resolving — button should be disabled, but guard defensively
    if (sessionLoading) return;
    const email = sessionUser?.email?.trim();
    if (!email) {
      // Remember they want Pro — persisted to sessionStorage so Google OAuth redirects don't lose it
      setPendingCheckout("pro");
      openAuthModal({ mode: "signup" });
      return;
    }
    setCheckoutPlan({ product: "pro", email });
  };

  const handlePpu = async () => {
    // Session still resolving — button should be disabled, but guard defensively
    if (sessionLoading) return;
    const email = sessionUser?.email?.trim();
    if (!email) {
      // Remember they want pay-per-use — persisted to sessionStorage so Google OAuth redirects don't lose it
      setPendingCheckout("ppu");
      openAuthModal({ mode: "signup" });
      return;
    }
    setCheckoutPlan({ product: "ppu", email, ppuQuantity: ppuQty });
  };

  const isPro  = currentPlan === "pro";

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <TopHeader />

      {/* ── Toast ── */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed top-20 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-xl text-sm font-medium shadow-lg ${
              toast.type === "success"
                ? "bg-emerald-500 text-white"
                : "bg-red-500 text-white"
            }`}
          >
            {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Hero ── */}
      <section className="pt-24 pb-16 px-4 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400 text-sm font-medium mb-6">
            <Sparkles size={14} />
            Simple, transparent pricing
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            Convert any API to an MCP.
            <br />
            <span className="bg-gradient-to-r from-blue-500 to-violet-500 bg-clip-text text-transparent">
              Free to start, Pro when you scale.
            </span>
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            No credit card required for the free tier. Upgrade to Pro for unlimited conversions and
            MCPs that never expire.
          </p>
        </motion.div>
      </section>

      {/* ── Pricing cards ── */}
      <section className="px-4 pb-20 max-w-6xl mx-auto w-full">
        <div className="grid md:grid-cols-3 gap-6 items-stretch">

          {/* Free */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="relative flex flex-col rounded-2xl border border-border bg-card p-8 shadow-sm"
          >
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 rounded-xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                  <Zap size={18} className="text-zinc-500" />
                </div>
                <span className="font-semibold text-lg">Free</span>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold">$0</span>
                <span className="text-muted-foreground text-sm">/ month</span>
              </div>
              <p className="text-sm text-muted-foreground">Perfect for trying it out.</p>
            </div>

            <ul className="space-y-3 flex-1 mb-8">
              {[
                "3 conversions / month",
                "Live MCP endpoint (7-day expiry)",
                "SSE + Streamable HTTP",
                "All API auth schemes",
              ].map(f => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                  <Check size={16} className="text-emerald-500 mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
              {[
                "Usage analytics",
                "Priority support",
              ].map(f => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-muted-foreground/50">
                  <X size={16} className="mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            <Link
              to="/"
              className="block w-full text-center py-3 rounded-xl border border-border text-sm font-medium hover:bg-muted/60 transition-colors"
            >
              Get started free
            </Link>
          </motion.div>

          {/* Pro — highlighted */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="relative flex flex-col rounded-2xl border-2 border-blue-500 bg-card p-8 shadow-xl ring-4 ring-blue-500/10 scale-[1.03]"
          >
            {/* Badge */}
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-4 py-1 rounded-full bg-gradient-to-r from-blue-500 to-violet-500 text-white text-xs font-bold tracking-wide shadow">
              <Star size={11} className="fill-white" />
              RECOMMENDED
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center shadow">
                  <Crown size={18} className="text-white" />
                </div>
                <span className="font-semibold text-lg">Pro</span>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold">$20</span>
                <span className="text-muted-foreground text-sm">/ month</span>
              </div>
              <p className="text-sm text-muted-foreground">For power users and teams.</p>
            </div>

            <ul className="space-y-3 flex-1 mb-8">
              {[
                "Unlimited conversions",
                "MCPs that never expire",
                "SSE + Streamable HTTP",
                "All API auth schemes",
                "Per-MCP usage analytics",
                "Priority API access",
                "Email support",
                "Early access to new tools",
              ].map(f => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-foreground">
                  <Check size={16} className="text-blue-500 mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            {isPro ? (
              <div className="flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 text-sm font-semibold">
                <Check size={16} />
                Current plan
              </div>
            ) : (
              <button
                onClick={handlePro}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-gradient-to-r from-blue-500 to-violet-500 text-white text-sm font-semibold hover:opacity-90 active:scale-[0.98] transition-all shadow-lg shadow-blue-500/25"
              >
                <CreditCard size={16} />
                Upgrade to Pro
                <ArrowRight size={15} />
              </button>
            )}
          </motion.div>

          {/* Pay-per-use */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="relative flex flex-col rounded-2xl border border-border bg-card p-8 shadow-sm"
          >
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 rounded-xl bg-amber-100 dark:bg-amber-950 flex items-center justify-center">
                  <BarChart3 size={18} className="text-amber-600 dark:text-amber-400" />
                </div>
                <span className="font-semibold text-lg">Pay-per-use</span>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold">$2</span>
                <span className="text-muted-foreground text-sm">/ 10 conversions</span>
              </div>
              <p className="text-sm text-muted-foreground">Top up when you need it.</p>
            </div>

            <ul className="space-y-3 flex-1 mb-6">
              {[
                "10 extra conversions per pack",
                "Credits never expire",
                "Same quality as free tier",
                "No subscription required",
              ].map(f => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                  <Check size={16} className="text-amber-500 mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            {/* Quantity picker */}
            <div className="flex items-center gap-3 mb-4">
              <label className="text-sm text-muted-foreground shrink-0">Packs:</label>
              <div className="flex items-center gap-1 flex-1">
                {[1, 2, 5].map(n => (
                  <button
                    key={n}
                    onClick={() => setPpuQty(n * 10)}
                    className={`flex-1 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                      ppuQty === n * 10
                        ? "border-amber-500 bg-amber-500/10 text-amber-600 dark:text-amber-400"
                        : "border-border text-muted-foreground hover:bg-muted/50"
                    }`}
                  >
                    {n}×
                  </button>
                ))}
              </div>
              <span className="text-sm font-bold text-foreground shrink-0">
                ${(ppuQty / 10) * 2}
              </span>
            </div>

            <button
              onClick={handlePpu}
              className="flex items-center justify-center gap-2 w-full py-3 rounded-xl border border-amber-500 text-amber-600 dark:text-amber-400 text-sm font-semibold hover:bg-amber-500/10 active:scale-[0.98] transition-all"
            >
              Buy {ppuQty} conversions
              <ArrowRight size={15} />
            </button>
          </motion.div>
        </div>

        {/* Trust badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="flex flex-wrap items-center justify-center gap-6 mt-10 text-sm text-muted-foreground"
        >
          {[
            { icon: <Shield size={15} />, label: "Secure payments" },
            { icon: <Infinity size={15} />, label: "Cancel anytime" },
            { icon: <Clock size={15} />, label: "Instant activation" },
          ].map(({ icon, label }) => (
            <div key={label} className="flex items-center gap-2">
              <span className="text-blue-500">{icon}</span>
              {label}
            </div>
          ))}
        </motion.div>
      </section>

      {/* ── Feature comparison table ── */}
      <section className="px-4 pb-20 max-w-4xl mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2 className="text-2xl font-bold text-center mb-8">Full feature comparison</h2>
          <div className="rounded-2xl border border-border overflow-hidden bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-6 py-4 font-semibold text-muted-foreground w-1/2">Feature</th>
                  <th className="text-center px-4 py-4 font-semibold w-1/4">Free</th>
                  <th className="text-center px-4 py-4 font-semibold w-1/4">
                    <span className="inline-flex items-center gap-1.5 text-blue-500">
                      <Crown size={14} />
                      Pro
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {FEATURES.map((f, i) => (
                  <tr
                    key={f.label}
                    className={`border-b border-border last:border-0 ${
                      i % 2 === 0 ? "bg-transparent" : "bg-muted/10"
                    }`}
                  >
                    <td className="px-6 py-3.5 text-foreground">{f.label}</td>
                    <td className="text-center px-4 py-3.5">
                      <FeatureValue val={f.free} />
                    </td>
                    <td className="text-center px-4 py-3.5">
                      <FeatureValue val={f.pro} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </section>

      {/* ── FAQ ── */}
      <section className="px-4 pb-24 max-w-3xl mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex items-center gap-2 justify-center mb-8">
            <HelpCircle size={20} className="text-blue-500" />
            <h2 className="text-2xl font-bold">Frequently asked questions</h2>
          </div>
          <div className="space-y-3">
            {FAQ.map(item => (
              <FaqItem key={item.q} {...item} />
            ))}
          </div>
        </motion.div>
      </section>

      {/* ── CTA strip ── */}
      <section className="px-4 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="max-w-3xl mx-auto rounded-2xl bg-gradient-to-br from-blue-600 to-violet-600 p-10 text-white text-center shadow-2xl"
        >
          <h2 className="text-3xl font-bold mb-3">Ready to convert your API?</h2>
          <p className="text-blue-100 mb-8 text-lg">
            Start for free. Upgrade when you need more power.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-2 px-8 py-3.5 rounded-xl bg-white text-blue-600 font-semibold hover:bg-blue-50 transition-colors shadow"
            >
              <Zap size={18} />
              Try for free
            </Link>
            {!isPro && (
              <button
                onClick={handlePro}
                className="flex items-center gap-2 px-8 py-3.5 rounded-xl bg-white/15 border border-white/30 text-white font-semibold hover:bg-white/25 transition-colors"
              >
                <Crown size={18} />
                Upgrade to Pro — $20/mo
              </button>
            )}
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
